import { PurchasePersistenceModel } from '@/core/domain/purchase-persistence/models/PurchasePersistence.model';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { DateTime } from 'luxon';
import { UrlService } from '@/common/url/url.service';
import { GetPurchaseUseCase } from '@/core/domain/purchase-persistence/usecases/get-purchase-persistence.usecase';
import { DeletePurchaseUseCase } from '@/core/domain/purchase-persistence/usecases/delete-purchase-persistence.usecase ';
import { TranslateService } from '@ngx-translate/core';

export const purchasePersistenceGuard: CanActivateFn = (route, state) => {
    const router = inject(Router);
    const url = inject(UrlService);
    const translate = inject(TranslateService);
    const deletePurchaseUseCase = inject(DeletePurchaseUseCase);
    const path = route.routeConfig?.path;
    return inject(GetPurchaseUseCase)
        .execute()
        .pipe(
            switchMap((purchasePersistence: PurchasePersistenceModel) => {
                if (!isValidDateExpiration(purchasePersistence)) {
                    return deletePurchaseUseCase
                        .execute()
                        .pipe(() => of('deletePurchaseUseCase'));
                }
                if (!isValidByUrl(purchasePersistence, path, translate)) {
                    navigateByUrl(path, router, url);
                }
                return of(true);
            }),
            switchMap((response) => {
                if (typeof response === 'boolean') {
                    return of(response);
                }
                if (response === 'deletePurchaseUseCase') {
                    navigateByUrl(path, router, url);
                }
                return of(false);
            }),
        );
};

const isValidByUrl = (
    purchasePersistence: PurchasePersistenceModel,
    route: string | undefined,
    translate: TranslateService,
) => {
    if (
        route === translate.instant('routes.amount') &&
        // eslint-disable-next-line no-prototype-builtins
        purchasePersistence.hasOwnProperty('cardDesign') &&
        purchasePersistence?.cardDesign !== undefined &&
        purchasePersistence?.cardDesign !== null
    ) {
        return true;
    }
    if (
        route === translate.instant('routes.send') &&
        // eslint-disable-next-line no-prototype-builtins
        purchasePersistence.hasOwnProperty('amount') &&
        purchasePersistence?.amount !== undefined &&
        purchasePersistence?.amount !== null &&
        purchasePersistence?.amount !== 0
    ) {
        return true;
    }
    if (
        route === translate.instant('routes.message') &&
        // eslint-disable-next-line no-prototype-builtins
        purchasePersistence.hasOwnProperty('shipment') &&
        purchasePersistence?.shipment !== undefined &&
        purchasePersistence?.shipment !== null
    ) {
        return true;
    }
    if (
        (route === translate.instant('routes.custom') ||
            route === translate.instant('routes.payment')) &&
        // eslint-disable-next-line no-prototype-builtins
        purchasePersistence.hasOwnProperty('message') &&
        purchasePersistence?.message !== undefined &&
        purchasePersistence?.message !== null
    ) {
        return true;
    }
    return false;
};

const isValidDateExpiration = (
    purchasePersistence: PurchasePersistenceModel,
): boolean => {
    if (typeof purchasePersistence?.expiration === 'string') {
        return !(
            DateTime.fromISO(purchasePersistence.expiration) < DateTime.now()
        );
    }
    return false;
};

const navigateByUrl = (
    route: string | undefined,
    router: Router,
    url: UrlService,
) => {
    if (route === 'amount') {
        url.format('purchase/design').subscribe((url) =>
            router.navigateByUrl(url),
        );
        return;
    }
    if (route === 'send') {
        url.format('purchase/amount').subscribe((url) =>
            router.navigateByUrl(url),
        );
        return;
    }
    if (route === 'message') {
        url.format('purchase/send').subscribe((url) =>
            router.navigateByUrl(url),
        );
        return;
    }
    if (route === 'custom') {
        url.format('purchase/message').subscribe((url) =>
            router.navigateByUrl(url),
        );
        return;
    }
    if (route === 'payment') {
        url.format('purchase/custom').subscribe((url) =>
            router.navigateByUrl(url),
        );
        return;
    }
    url.format('purchase/design').subscribe((url) => router.navigateByUrl(url));
};
